import React from 'react';
import './login-platter.scss';
import usePlatterPlay from './usePlatterPlay';

interface LoginPlatterProps {
  version: string;
  flameSrc?: string;
  handOpenSrc?: string;
  handGrabSrc?: string;
}

const PlatterHand: React.FC<{
  x: number;
  y: number;
  grabbing: boolean;
  openSrc?: string;
  grabSrc?: string;
}> = ({ x, y, grabbing, openSrc, grabSrc }) => {
  const src = grabbing ? grabSrc || openSrc : openSrc || grabSrc;
  if (!src) return null;

  return (
    <img
      src={src}
      alt=""
      draggable={false}
      aria-hidden="true"
      className={
        grabbing ? 'login-platter-hand is-grabbing' : 'login-platter-hand'
      }
      style={{ left: x, top: y }}
    />
  );
};

const LoginPlatter: React.FC<LoginPlatterProps> = ({
  version,
  flameSrc,
  handOpenSrc,
  handGrabSrc,
}) => {
  const {
    discRef,
    stopped,
    scratching,
    dragging,
    hand,
    demoGrab,
    orbitOrigin,
    dragRotate,
    onPointerMove,
    onPointerEnter,
    onPointerLeave,
    onPointerDown,
    onPointerUp,
    onPointerCancel,
  } = usePlatterPlay();

  return (
    <>
      <div className="login-platter-clip">
        <div className="login-platter-position">
          <div
            className={
              scratching
                ? 'login-platter-scratch is-scratching'
                : 'login-platter-scratch'
            }
          >
            <div
              className="login-platter-turn"
              style={{ transform: `rotate(${dragRotate}deg)` }}
            >
              <div
                ref={discRef}
                role="img"
                aria-label="Vinyl record, drag to scratch"
                className={
                  stopped
                    ? 'login-platter-disc is-stopped'
                    : 'login-platter-disc'
                }
                onPointerEnter={onPointerEnter}
                onPointerLeave={onPointerLeave}
                onPointerMove={onPointerMove}
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
              >
                <div className="login-platter-grooves-a" />
                <div className="login-platter-grooves-b" />
                <div className="login-platter-specular-a" />
                <div className="login-platter-specular-b" />
                <div className="login-platter-bounce" />
                <div className="login-platter-falloff" />
                <div className="login-platter-rim" />
                <div className="login-platter-label">
                  <svg viewBox="0 0 200 200">
                    <circle
                      cx="100"
                      cy="100"
                      r="92"
                      fill="none"
                      stroke="rgba(255,238,214,.14)"
                      strokeWidth="0.7"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="64"
                      fill="none"
                      stroke="rgba(255,238,214,.1)"
                      strokeWidth="0.6"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="42"
                      fill="none"
                      stroke="rgba(211,119,28,.55)"
                      strokeWidth="0.9"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="38.5"
                      fill="none"
                      stroke="rgba(255,238,214,.08)"
                      strokeWidth="0.5"
                    />
                    <text
                      x="27"
                      y="98.6"
                      textAnchor="middle"
                      className="login-platter-meta-strong"
                    >
                      VERSION
                    </text>
                    <text
                      x="27"
                      y="105.4"
                      textAnchor="middle"
                      className="login-platter-meta"
                    >
                      {version}
                    </text>
                    <text
                      x="173"
                      y="98.6"
                      textAnchor="middle"
                      className="login-platter-meta-strong"
                    >
                      33⅓ RPM
                    </text>
                    <text
                      x="173"
                      y="105.4"
                      textAnchor="middle"
                      className="login-platter-meta"
                    >
                      SIDE A
                    </text>
                  </svg>
                  {flameSrc ? (
                    <img src={flameSrc} alt="" className="login-platter-mark" />
                  ) : null}
                  <div className="login-platter-spindle" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {hand ? (
        <div
          className={
            scratching
              ? 'login-platter-hand-orbit is-scratching'
              : 'login-platter-hand-orbit'
          }
          style={
            orbitOrigin
              ? { transformOrigin: `${orbitOrigin.x}px ${orbitOrigin.y}px` }
              : undefined
          }
        >
          <PlatterHand
            x={hand.x}
            y={hand.y}
            grabbing={dragging || demoGrab}
            openSrc={handOpenSrc}
            grabSrc={handGrabSrc}
          />
        </div>
      ) : null}
      <div className="login-scrim" aria-hidden="true" />
      <div className="login-glow" aria-hidden="true" />
    </>
  );
};

export default LoginPlatter;
